import { Reducer, useReducer } from "react";

export const STEPS = {
  ORGANIZATION: "organization",
  ACADEMIC_YEAR: "academic_year",
  GRADUATION_CLASS: "graduation_class",
  ADD_STUDENTS: "add_students",
  COMPLETE: "complete",
};

export const ACTIONS = {
  NEXT_STEP: "nextStep",
  PREVIOUS_STEP: "previousStep",
  GO_TO_STEP: "goToStep",
  COMPLETE_ORGANIZATION: "completeOrganization",
  COMPLETE_ACADEMIC_YEAR: "completeAcademicYear",
  COMPLETE_GRADUATION_CLASS: "completeGraduationClass",
  COMPLETE_ADD_STUDENTS: "completeAddStudents",
  RESET: "reset",
};

// Orden de los pasos
const STEP_ORDER = [
  STEPS.ORGANIZATION,
  STEPS.ACADEMIC_YEAR,
  STEPS.GRADUATION_CLASS,
  STEPS.ADD_STUDENTS,
  STEPS.COMPLETE,
];

// Mapeo de pasos a índices
export const STEP_TO_INDEX = STEP_ORDER.reduce(
  (acc, step, index) => {
    acc[step] = index;
    return acc;
  },
  {} as Record<string, number>,
);

interface OnboardingState {
  currentStep: string;
  currentStepIndex: number;
  organizationId?: string;
  academicYearId?: string;
  graduationClassId?: string;
  completedSteps: string[];
}

const initialState: OnboardingState = {
  currentStep: STEPS.ORGANIZATION,
  currentStepIndex: 0,
  completedSteps: [],
};

export const useOnBoardingStepManager = () => {
  function reducer(state: OnboardingState, action: any): OnboardingState {
    switch (action.type) {
      case ACTIONS.NEXT_STEP: {
        const nextIndex = Math.min(
          state.currentStepIndex + 1,
          STEP_ORDER.length - 1,
        );
        return {
          ...state,
          currentStep: STEP_ORDER[nextIndex],
          currentStepIndex: nextIndex,
        };
      }

      case ACTIONS.PREVIOUS_STEP: {
        const prevIndex = Math.max(state.currentStepIndex - 1, 0);
        return {
          ...state,
          currentStep: STEP_ORDER[prevIndex],
          currentStepIndex: prevIndex,
        };
      }

      case ACTIONS.GO_TO_STEP: {
        const targetStep = action.payload;
        const targetIndex = STEP_TO_INDEX[targetStep];
        if (targetIndex !== undefined) {
          return {
            ...state,
            currentStep: targetStep,
            currentStepIndex: targetIndex,
          };
        }
        return state;
      }

      case ACTIONS.COMPLETE_ORGANIZATION: {
        const nextIndex = state.currentStepIndex + 1;
        return {
          ...state,
          organizationId: action.payload.organizationId,
          completedSteps: [...state.completedSteps, STEPS.ORGANIZATION],
          currentStep: STEP_ORDER[nextIndex],
          currentStepIndex: nextIndex,
        };
      }

      case ACTIONS.COMPLETE_ACADEMIC_YEAR: {
        const nextIndex = state.currentStepIndex + 1;
        return {
          ...state,
          academicYearId: action.payload.academicYearId,
          completedSteps: [...state.completedSteps, STEPS.ACADEMIC_YEAR],
          currentStep: STEP_ORDER[nextIndex],
          currentStepIndex: nextIndex,
        };
      }

      case ACTIONS.COMPLETE_GRADUATION_CLASS: {
        const nextIndex = state.currentStepIndex + 1;
        return {
          ...state,
          graduationClassId: action.payload.graduationClassId,
          completedSteps: [...state.completedSteps, STEPS.GRADUATION_CLASS],
          currentStep: STEP_ORDER[nextIndex],
          currentStepIndex: nextIndex,
        };
      }

      case ACTIONS.COMPLETE_ADD_STUDENTS: {
        const nextIndex = state.currentStepIndex + 1;
        return {
          ...state,
          completedSteps: [...state.completedSteps, STEPS.ADD_STUDENTS],
          currentStep: STEP_ORDER[nextIndex],
          currentStepIndex: nextIndex,
        };
      }

      case ACTIONS.RESET: {
        return initialState;
      }

      default:
        return state;
    }
  }

  const [state, dispatch] = useReducer<Reducer<OnboardingState, any>>(
    reducer,
    initialState,
  );

  // Acciones helper
  const nextStep = () => dispatch({ type: ACTIONS.NEXT_STEP });
  const previousStep = () => dispatch({ type: ACTIONS.PREVIOUS_STEP });
  const goToStep = (step: string) =>
    dispatch({ type: ACTIONS.GO_TO_STEP, payload: step });

  const completeOrganization = (organizationId: string) =>
    dispatch({
      type: ACTIONS.COMPLETE_ORGANIZATION,
      payload: { organizationId },
    });

  const completeAcademicYear = (academicYearId: string) =>
    dispatch({
      type: ACTIONS.COMPLETE_ACADEMIC_YEAR,
      payload: { academicYearId },
    });

  const completeGraduationClass = (graduationClassId: string) =>
    dispatch({
      type: ACTIONS.COMPLETE_GRADUATION_CLASS,
      payload: { graduationClassId },
    });

  const completeAddStudents = () =>
    dispatch({ type: ACTIONS.COMPLETE_ADD_STUDENTS });

  const reset = () => dispatch({ type: ACTIONS.RESET });

  return {
    state,
    dispatch,
    // Helper functions
    nextStep,
    previousStep,
    goToStep,
    completeOrganization,
    completeAcademicYear,
    completeGraduationClass,
    completeAddStudents,
    reset,
    // Computed values
    isFirstStep: state.currentStepIndex === 0,
    isLastStep: state.currentStepIndex === STEP_ORDER.length - 1,
    canGoBack: state.currentStepIndex > 0,
    canGoNext: state.currentStepIndex < STEP_ORDER.length - 1,
  };
};
